<template>
  <div class="login-container">
    <!-- 动态背景圆圈 -->
    <div class="background-circles">
      <div 
        v-for="i in 10" 
        :key="i" 
        class="circle" 
        :class="`circle-${i}`"
      ></div>
    </div>
    
    <!-- 登录卡片 -->
    <div class="login-card">
      <div class="login-header">
        <h2 class="login-title">工单管理系统</h2>
        <p class="login-subtitle">欢迎回来，请登录您的账户</p>
      </div>
      
      <div class="login-form">
        <a-form :model="formData" @submit="handleSubmit" :label-col-props="{ span: 0 }" :wrapper-col-props="{span:24}" >
          <a-form-item field="username" validate-trigger="blur">
            <a-input v-model="formData.username" placeholder="请输入账号" size="large" class="custom-input">
              <template #prefix>
                <icon-user />
              </template>
            </a-input>
          </a-form-item>
          <a-form-item field="password" validate-trigger="blur">
            <a-input-password v-model="formData.password" placeholder="请输入密码" size="large" class="custom-input">
              <template #prefix>
                <icon-lock />
              </template>
            </a-input-password>
          </a-form-item>
          <a-form-item field="captcha" validate-trigger="blur">
            <div class="captcha-container">
              <a-input v-model="formData.captcha" placeholder="请输入验证码" size="large" class="custom-input captcha-input">
                <template #prefix>
                  <icon-safe />
                </template>
              </a-input>
              <img :src="captchaImageUrl" alt="Captcha" @click="refreshCaptcha" class="captcha-image" />
            </div>
          </a-form-item>
          <div class="login-options">
            <a-space>
              <a-switch v-model="formData.remember" type="round" size="small" />
              <span class="remember-text">记住我</span>
            </a-space>
            <a href="#" class="forgot-password">忘记密码？</a>
          </div>
          <ZlButton variant="primary" glass html-type="submit" long :loading="loading" size="large">登录</ZlButton>
          
          <!-- 移动到登录按钮下方的logo -->
          <div class="logo-footer">
            <span class="logo-icon">🏠</span>
            <span class="logo-text">vip-二斤口  （2026-09-01）</span>
          </div>
        </a-form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import { IconUser, IconLock, IconSafe } from '@arco-design/web-vue/es/icon';
import { Message,Notification } from '@arco-design/web-vue';
import {useRoute, useRouter} from 'vue-router';
import { doLogin, getCaptcha} from "@/apis/LoginApi";
import { useUserStore } from "@/store/user";
import ZlButton from '@/components/ZlButton.vue';

const store = useUserStore();
const route = useRoute();
const router = useRouter();
const loading = ref(false);
const formData = reactive({
  username: '',
  password: '',
  captcha: '',
  captchaKey: '',
  remember: true
});

const captchaImageUrl = ref('');

const refreshCaptcha = () => {
  // 发起请求获取验证码图片
  getCaptcha().then((res)=>{
    // 更新验证码图片URL
    console.log(res)
    if (res.code===0){
      captchaImageUrl.value = res.data.image;
      formData.captchaKey = res.data.key;
    }else {
      Message.error(res.msg);
    }
  })
};

onMounted(() => {
  refreshCaptcha();
});

const handleSubmit = async () => {
  loading.value = true;
  try {
    // 这里添加实际的登录逻辑
    if (!formData.username || !formData.password) {
      Message.warning('请输入用户名和密码');
      loading.value = false;
      return;
    }

    // Mock captcha validation
    if (!formData.captcha || formData.captcha.length < 4) {
      Message.error('请输入验证码');
      refreshCaptcha(); // Refresh captcha on error
      loading.value = false;
      return;
    }
    
    // 使用 async/await 语法处理登录请求
    const res = await doLogin(formData);
    if (res.code===0) {
      Message.success('登录成功');
      // 保存到pinia
      store.updateToken(res.data.token);
      // 检查是否有重定向地址
      const redirect = route.query.redirect || '/workorder/list';
      // 使用 await 确保路由跳转完成
      await router.push(redirect);
    } else {
      Message.error(res.message);
      refreshCaptcha(); // Refresh captcha on error
    }
  } catch (error) {
    Message.error('登录失败');
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.login-container {
  position: relative;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  background: linear-gradient(135deg, #f8f9fa 0%, #e8f5e8 100%);
  background-size: 400% 400%;
  animation: gradientShift 15s ease infinite;
  display: flex;
  align-items: center;
  justify-content: center;
}

@keyframes gradientShift {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

.background-circles {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
}

.circle {
  position: absolute;
  border-radius: 50%;
  opacity: 0.8;
  filter: blur(0.5px);
}

.circle-1 {
  width: 140px;
  height: 140px;
  background: radial-gradient(circle, rgba(74, 144, 226, 0.5) 0%, rgba(74, 144, 226, 0.2) 70%, transparent 100%);
  top: 10%;
  left: 15%;
  animation: float-1 25s ease-in-out infinite;
}

.circle-2 {
  width: 100px;
  height: 100px;
  background: radial-gradient(circle, rgba(156, 39, 176, 0.5) 0%, rgba(156, 39, 176, 0.2) 70%, transparent 100%);
  top: 60%;
  right: 20%;
  animation: float-2 30s ease-in-out infinite;
}

.circle-3 {
  width: 170px;
  height: 170px;
  background: radial-gradient(circle, rgba(255, 193, 7, 0.45) 0%, rgba(255, 193, 7, 0.18) 70%, transparent 100%);
  bottom: 15%;
  left: 25%;
  animation: float-3 35s ease-in-out infinite;
}

.circle-4 {
  width: 120px;
  height: 120px;
  background: radial-gradient(circle, rgba(76, 175, 80, 0.5) 0%, rgba(76, 175, 80, 0.2) 70%, transparent 100%);
  top: 30%;
  right: 10%;
  animation: float-4 28s ease-in-out infinite;
}

.circle-5 {
  width: 80px;
  height: 80px;
  background: radial-gradient(circle, rgba(255, 87, 34, 0.55) 0%, rgba(255, 87, 34, 0.22) 70%, transparent 100%);
  top: 80%;
  left: 60%;
  animation: float-5 22s ease-in-out infinite;
}

.circle-6 {
  width: 130px;
  height: 130px;
  background: radial-gradient(circle, rgba(63, 81, 181, 0.48) 0%, rgba(63, 81, 181, 0.19) 70%, transparent 100%);
  top: 5%;
  left: 70%;
  animation: float-6 32s ease-in-out infinite;
}

.circle-7 {
  width: 110px;
  height: 110px;
  background: radial-gradient(circle, rgba(233, 30, 99, 0.52) 0%, rgba(233, 30, 99, 0.21) 70%, transparent 100%);
  bottom: 40%;
  right: 5%;
  animation: float-7 26s ease-in-out infinite;
}

.circle-8 {
  width: 90px;
  height: 90px;
  background: radial-gradient(circle, rgba(0, 188, 212, 0.5) 0%, rgba(0, 188, 212, 0.2) 70%, transparent 100%);
  top: 45%;
  left: 5%;
  animation: float-8 29s ease-in-out infinite;
}

.circle-9 {
  width: 150px;
  height: 150px;
  background: radial-gradient(circle, rgba(139, 195, 74, 0.45) 0%, rgba(139, 195, 74, 0.18) 70%, transparent 100%);
  bottom: 10%;
  right: 40%;
  animation: float-9 33s ease-in-out infinite;
}

.circle-10 {
  width: 105px;
  height: 105px;
  background: radial-gradient(circle, rgba(121, 85, 72, 0.5) 0%, rgba(121, 85, 72, 0.2) 70%, transparent 100%);
  top: 20%;
  left: 40%;
  animation: float-10 27s ease-in-out infinite;
}

/* 动画关键帧 */
@keyframes float-1 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  25% { transform: translate(30px, -20px) scale(1.1); }
  50% { transform: translate(-20px, 40px) scale(0.9); }
  75% { transform: translate(40px, 20px) scale(1.05); }
}

@keyframes float-2 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(-40px, 30px) scale(1.15); }
  66% { transform: translate(25px, -35px) scale(0.85); }
}

@keyframes float-3 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  20% { transform: translate(20px, 30px) scale(1.08); }
  40% { transform: translate(-30px, -20px) scale(0.92); }
  60% { transform: translate(35px, 15px) scale(1.12); }
  80% { transform: translate(-15px, 25px) scale(0.88); }
}

@keyframes float-4 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  30% { transform: translate(-25px, -30px) scale(1.1); }
  70% { transform: translate(35px, 25px) scale(0.9); }
}

@keyframes float-5 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  25% { transform: translate(15px, -25px) scale(1.2); }
  50% { transform: translate(-30px, 20px) scale(0.8); }
  75% { transform: translate(20px, 30px) scale(1.1); }
}

@keyframes float-6 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  16% { transform: translate(-20px, 25px) scale(1.05); }
  33% { transform: translate(30px, -15px) scale(0.95); }
  50% { transform: translate(-35px, -25px) scale(1.1); }
  66% { transform: translate(25px, 35px) scale(0.9); }
  83% { transform: translate(15px, -30px) scale(1.03); }
}

@keyframes float-7 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  40% { transform: translate(30px, 20px) scale(1.08); }
  80% { transform: translate(-25px, -30px) scale(0.92); }
}

@keyframes float-8 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  35% { transform: translate(-30px, 35px) scale(1.15); }
  70% { transform: translate(40px, -25px) scale(0.85); }
}

@keyframes float-9 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  25% { transform: translate(25px, 30px) scale(1.06); }
  50% { transform: translate(-35px, -20px) scale(0.94); }
  75% { transform: translate(30px, -35px) scale(1.08); }
}

@keyframes float-10 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  30% { transform: translate(-20px, -25px) scale(1.12); }
  60% { transform: translate(35px, 30px) scale(0.88); }
}

.login-card {
  position: relative;
  z-index: 2;
  width: 450px;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(25px) saturate(200%);
  -webkit-backdrop-filter: blur(25px) saturate(200%);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 24px;
  padding: 40px;
  box-shadow: 
    0 25px 60px rgba(0, 0, 0, 0.15),
    0 15px 35px rgba(0, 0, 0, 0.1),
    0 8px 20px rgba(0, 0, 0, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.4);
  transition: transform 0.3s ease;
}

.login-header {
  text-align: center;
  margin-bottom: 35px;
}

.logo-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
}

.logo-footer .logo-icon {
  font-size: 14px;
  margin-right: 6px;
}

.logo-footer .logo-text {
  font-size: 12px;
  font-weight: 400;
  color: #7f8c8d;
  opacity: 0.8;
}

.login-title {
  font-size: 28px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 8px 0;
}

.login-subtitle {
  font-size: 16px;
  color: #7f8c8d;
  margin: 0;
}

.login-form {
  width: 100%;
}

:deep(.custom-input) {
  background: rgba(255, 255, 255, 0.9) !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  border-radius: 12px !important;
  transition: all 0.3s ease !important;
}

:deep(.custom-input:hover) {
  background: rgba(255, 255, 255, 0.95) !important;
  border-color: rgba(168, 200, 236, 0.5) !important;
  box-shadow: 0 4px 15px rgba(168, 200, 236, 0.2) !important;
}

:deep(.custom-input:focus-within) {
  background: rgba(255, 255, 255, 1) !important;
  border-color: #a8c8ec !important;
  box-shadow: 0 0 0 3px rgba(168, 200, 236, 0.2) !important;
}

.captcha-container {
  display: flex;
  gap: 12px;
  align-items: center;
}

.captcha-input {
  flex: 1;
}

.captcha-image {
  height: 40px;
  width: 120px;
  cursor: pointer;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  transition: all 0.3s ease;
}

.captcha-image:hover {
  border-color: #a8c8ec;
  box-shadow: 0 4px 15px rgba(168, 200, 236, 0.3);
}

.login-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 20px 0 25px 0;
}

.remember-text {
  color: #2c3e50;
  font-size: 14px;
}

.forgot-password {
  color: #a8c8ec;
  text-decoration: none;
  font-size: 14px;
  transition: color 0.3s ease;
}

.forgot-password:hover {
  color: #667eea;
  text-decoration: underline;
}

:deep(.arco-form-item) {
  margin-bottom: 20px;
}

:deep(.arco-form-item-content) {
  display: flex;
  align-items: center;
}
</style>